const DEFAULT_SETTINGS = {
  // Neutral/no-effect defaults
  bass: 0,
  bassFrequency: 120,
  pitch: 0,
  // Additional tone shaping
  formant: 0, // semitones (-12..+12)
  brightness: 0, // dB tilt via highshelf (and mild lowshelf compensation)
  presence: 0, // dB peaking around 3 kHz
  timbreIntensity: 1.0, // scales brightness/presence/formant gains (0..2)
  vocoderMode: false,
  echo: 0,
  echoDelay: 0.18,
  wet: 0,
  dry: 1
}

const VOICE_WORKLET_URL = new URL('../worklets/voice-processor.js', import.meta.url).toString()
const RUBBERBAND_BRIDGE_URL = new URL(
  '../worklets/rubberband-bridge.js',
  import.meta.url
).toString()

function createPitchShifterNode(audioContext) {
  // Lightweight granular/LFO-based pitch shifter built from core WebAudio nodes.
  // Approach: two modulated delay lines with crossfade to avoid discontinuities.
  // This is synchronous (no AudioWorklet needed) and good enough for ±12 semitones.

  const input = audioContext.createGain()
  const output = audioContext.createGain()

  // Two delay lines
  const maxDelay = 0.08 // 80ms buffer
  const minDelay = 0.006 // guard against 0 to reduce clicks
  const dA = audioContext.createDelay(maxDelay)
  const dB = audioContext.createDelay(maxDelay)

  // Crossfade gains
  const gA = audioContext.createGain()
  const gB = audioContext.createGain()
  gA.gain.value = 0.5
  gB.gain.value = 0.5

  // LFOs for delay modulation (sawtooth for delay, triangle for crossfade)
  const lfoDelayA = audioContext.createOscillator()
  const lfoDelayB = audioContext.createOscillator()
  const lfoXfade = audioContext.createOscillator()
  lfoDelayA.type = 'sawtooth'
  lfoDelayB.type = 'sawtooth'
  lfoXfade.type = 'triangle'

  // Scale [-1,1] -> [0,1]
  const to01A = audioContext.createGain()
  const to01B = audioContext.createGain()
  const to01X = audioContext.createGain()
  to01A.gain.value = 0.5
  to01B.gain.value = 0.5
  to01X.gain.value = 0.5
  const offA = audioContext.createConstantSource()
  const offB = audioContext.createConstantSource()
  const offX = audioContext.createConstantSource()
  offA.offset.value = 0.5
  offB.offset.value = 0.5
  offX.offset.value = 0.5

  // Map [0,1] -> [minDelay, maxDelay]
  const delayScaleA = audioContext.createGain()
  const delayScaleB = audioContext.createGain()
  delayScaleA.gain.value = maxDelay - minDelay
  delayScaleB.gain.value = maxDelay - minDelay
  const delayOffsetA = audioContext.createConstantSource()
  const delayOffsetB = audioContext.createConstantSource()
  delayOffsetA.offset.value = minDelay
  delayOffsetB.offset.value = minDelay

  // Build graph
  input.connect(dA)
  input.connect(dB)
  dA.connect(gA)
  dB.connect(gB)
  gA.connect(output)
  gB.connect(output)

  // LFO routing for delay modulation
  lfoDelayA.connect(to01A)
  to01A.connect(delayScaleA)
  delayScaleA.connect(dA.delayTime)
  offA.connect(dA.delayTime)

  lfoDelayB.connect(to01B)
  to01B.connect(delayScaleB)
  delayScaleB.connect(dB.delayTime)
  offB.connect(dB.delayTime)

  // Crossfade control: triangle 0..1 -> gains (A = x, B = 1-x)
  lfoXfade.connect(to01X)
  const xToA = audioContext.createGain()
  xToA.gain.value = 1
  const invX = audioContext.createGain()
  invX.gain.value = -1
  const invXAdd = audioContext.createConstantSource()
  invXAdd.offset.value = 1
  to01X.connect(xToA)
  xToA.connect(gA.gain)
  to01X.connect(invX)
  invX.connect(gB.gain)
  invXAdd.connect(gB.gain)

  // Phase offset B by 180° (invert saw) so its read head runs opposite of A
  const invertB = audioContext.createGain()
  invertB.gain.value = -1
  lfoDelayA.connect(invertB)
  invertB.connect(lfoDelayB.frequency) // use frequency inversion linkage guard below

  // We'll manage frequencies explicitly; disconnect the above linkage
  try {
    lfoDelayA.disconnect(invertB)
  } catch {}

  // Start sources
  offA.start()
  offB.start()
  offX.start()
  lfoDelayA.start()
  lfoDelayB.start()
  lfoXfade.start()

  // State
  let currentSemitones = 0

  const setPitch = (semitones = 0) => {
    currentSemitones = Number(semitones) || 0
    // Convert semitones to ratio
    const r = Math.pow(2, currentSemitones / 12)
    const atTime = audioContext.currentTime + 0.02

    if (Math.abs(currentSemitones) < 0.001) {
      // Near zero: hold delays steady, equal crossfade, very slow LFOs to minimize motion
      const holdDelay = minDelay + (maxDelay - minDelay) * 0.5
      dA.delayTime.setTargetAtTime(holdDelay, atTime, 0.02)
      dB.delayTime.setTargetAtTime(holdDelay, atTime, 0.02)
      gA.gain.setTargetAtTime(0.5, atTime, 0.02)
      gB.gain.setTargetAtTime(0.5, atTime, 0.02)
      lfoDelayA.frequency.setTargetAtTime(0.0001, atTime, 0.02)
      lfoDelayB.frequency.setTargetAtTime(0.0001, atTime, 0.02)
      lfoXfade.frequency.setTargetAtTime(0.0001, atTime, 0.02)
      return
    }

    // For a linear sweep of delay 0..(maxDelay-minDelay) over T seconds, the
    // instantaneous pitch ratio ~ 1 / (1 ∓ slope), slope = (max-min)/T.
    // Solve for T given desired ratio r: slope = |1 - 1/r| (up) or |r - 1| (down)
    const swing = maxDelay - minDelay
    const isUp = currentSemitones > 0
    const slope = isUp ? Math.abs(1 - 1 / r) : Math.abs(r - 1)
    const T = Math.max(0.02, swing / Math.max(1e-4, slope))
    const freq = Math.max(0.1, Math.min(12, 1 / T))

    // Modulate delays in opposite directions
    lfoDelayA.frequency.setTargetAtTime(freq, atTime, 0.02)
    lfoDelayB.frequency.setTargetAtTime(freq, atTime, 0.02)
    // Reverse one LFO by 180° using detune offset
    // Use detune to offset phase subtly at start; ongoing phase difference comes from start order
    lfoDelayA.detune.setTargetAtTime(isUp ? 0 : 0, atTime, 0.02)
    lfoDelayB.detune.setTargetAtTime(180, atTime, 0.02)

    // Crossfade in sync with delay sweeps
    lfoXfade.frequency.setTargetAtTime(freq, atTime, 0.02)
  }

  // Initialize neutral
  setPitch(0)

  return { input, output, setPitch }
}

export function createVoiceGraph(audioContext, mediaStream, settings = {}) {
  if (!audioContext) throw new Error('audioContext is required')
  if (!mediaStream) throw new Error('mediaStream is required')

  const opts = { ...DEFAULT_SETTINGS, ...settings }
  const source = audioContext.createMediaStreamSource(mediaStream)

  const bassFilter = audioContext.createBiquadFilter()
  bassFilter.type = 'lowshelf'
  bassFilter.frequency.value = opts.bassFrequency
  bassFilter.gain.value = opts.bass

  // Brightness tilt: highshelf with mild lowshelf counter to preserve loudness
  const highShelf = audioContext.createBiquadFilter()
  highShelf.type = 'highshelf'
  highShelf.frequency.value = 3500
  highShelf.gain.value = opts.brightness

  const lowTiltComp = audioContext.createBiquadFilter()
  lowTiltComp.type = 'lowshelf'
  lowTiltComp.frequency.value = 200
  lowTiltComp.gain.value = -opts.brightness * 0.6

  // Presence peak around 3kHz
  const presencePeak = audioContext.createBiquadFilter()
  presencePeak.type = 'peaking'
  presencePeak.frequency.value = 3000
  presencePeak.Q.value = 1.2
  presencePeak.gain.value = opts.presence * (opts.timbreIntensity ?? 1)

  // Simple formant shaper: three peaking filters whose centers shift by semitone ratio
  const baseFormants = [500, 1500, 2500]
  const formantQs = [1.3, 1.1, 0.9]
  const formantGains = [8, 6, 4] // dB emphasis per band for stronger effect
  const formantFilters = baseFormants.map((f, i) => {
    const biq = audioContext.createBiquadFilter()
    biq.type = 'peaking'
    biq.frequency.value = f
    biq.Q.value = formantQs[i]
    biq.gain.value = formantGains[i] * (opts.timbreIntensity ?? 1)
    return biq
  })

  const pitchNode = createPitchShifterNode(audioContext)

  const delay = audioContext.createDelay(1.0)
  delay.delayTime.value = opts.echoDelay
  const feedbackGain = audioContext.createGain()
  feedbackGain.gain.value = opts.echo

  const wetGain = audioContext.createGain()
  const dryGain = audioContext.createGain()
  wetGain.gain.value = opts.wet
  dryGain.gain.value = opts.dry

  const destination = audioContext.createMediaStreamDestination()

  // Graph: source -> bass -> brightness tilt -> presence -> formants -> pitch (or worklet) -> delay -> ...
  source.connect(bassFilter)
  bassFilter.connect(lowTiltComp)
  lowTiltComp.connect(highShelf)
  highShelf.connect(presencePeak)
  // chain formant filters sequentially
  let last = presencePeak
  formantFilters.forEach((f) => {
    last.connect(f)
    last = f
  })
  const prePitchEnd = last
  prePitchEnd.connect(pitchNode.input)
  pitchNode.output.connect(delay)
  // Gentle exciter path (adds high-frequency harmonics for clearer shifted voices)
  const exciterPreHp = audioContext.createBiquadFilter()
  exciterPreHp.type = 'highpass'
  exciterPreHp.frequency.value = 1200
  const exciterShaper = audioContext.createWaveShaper()
  const exciterPostLp = audioContext.createBiquadFilter()
  exciterPostLp.type = 'lowpass'
  exciterPostLp.frequency.value = 8000
  const exciterGain = audioContext.createGain()
  exciterGain.gain.value = opts.vocoderMode ? 0.15 : 0.0

  // Build a soft saturation curve for the exciter
  const curveLen = 2048
  const curve = new Float32Array(curveLen)
  for (let i = 0; i < curveLen; i++) {
    const x = (i / (curveLen - 1)) * 2 - 1
    // soft clip: y = x / (1 + a*|x|), a=1.5
    const a = 1.5
    curve[i] = x / (1 + a * Math.abs(x))
  }
  exciterShaper.curve = curve
  exciterShaper.oversample = '2x'

  // Parallel exciter from pitch output
  pitchNode.output.connect(exciterPreHp)
  exciterPreHp.connect(exciterShaper)
  exciterShaper.connect(exciterPostLp)
  exciterPostLp.connect(exciterGain)
  exciterGain.connect(destination)

  delay.connect(feedbackGain)
  feedbackGain.connect(delay)
  delay.connect(wetGain)
  pitchNode.output.connect(dryGain)
  wetGain.connect(destination)
  dryGain.connect(destination)

  // Optionally swap in an AudioWorklet-based processor when vocoderMode is enabled.
  let workletNode = null
  let usingWorklet = false

  const trySetupWorklet = async () => {
    if (!opts.vocoderMode) return
    if (!audioContext?.audioWorklet) return
    try {
      await audioContext.audioWorklet.addModule(VOICE_WORKLET_URL)
      workletNode = new AudioWorkletNode(audioContext, 'voice-processor')
      // Prime parameters
      try {
        workletNode.parameters
          .get('pitch')
          ?.setValueAtTime(opts.pitch || 0, audioContext.currentTime)
        workletNode.parameters
          .get('formant')
          ?.setValueAtTime(opts.formant || 0, audioContext.currentTime)
        workletNode.parameters.get('wet')?.setValueAtTime(1, audioContext.currentTime)
        workletNode.parameters.get('dry')?.setValueAtTime(0, audioContext.currentTime)
      } catch {}

      // Rewire: prePitchEnd -> worklet -> delay/dry; detach pitchNode from chain
      try {
        prePitchEnd.disconnect()
      } catch {}
      try {
        pitchNode.input.disconnect()
      } catch {}
      try {
        pitchNode.output.disconnect()
      } catch {}
      prePitchEnd.connect(workletNode)
      workletNode.connect(delay)
      workletNode.connect(dryGain)
      // Ask the worklet to load Rubber Band bridge and WASM assets (placeholder URLs)
      try {
        workletNode.port.postMessage({
          type: 'load-rubberband',
          wasm: '/worklets/rubberband.wasm',
          js: RUBBERBAND_BRIDGE_URL,
          channels: 1,
          preserveFormants: true
        })
      } catch {}
      usingWorklet = true
    } catch (e) {
      // Fallback silently if loading fails
      usingWorklet = false
    }
  }

  // Fire-and-forget setup (does not block createVoiceGraph)
  trySetupWorklet()

  const updateSettings = (next = {}) => {
    const merged = { ...opts, ...next }
    bassFilter.gain.value = merged.bass
    bassFilter.frequency.value = merged.bassFrequency
    highShelf.gain.value = merged.brightness * (merged.timbreIntensity ?? 1)
    lowTiltComp.gain.value = -(merged.brightness * (merged.timbreIntensity ?? 1)) * 0.6
    presencePeak.gain.value = merged.presence * (merged.timbreIntensity ?? 1)
    // Update formant centers by semitone ratio
    try {
      const fSemi = Number(merged.formant) || 0
      const preserve = merged.vocoderMode ? Number(merged.pitch) || 0 : 0
      const effectiveSemi = fSemi - preserve
      const ratio = Math.pow(2, effectiveSemi / 12)
      baseFormants.forEach((base, i) => {
        const biq = formantFilters[i]
        if (biq) {
          biq.frequency.value = Math.max(80, Math.min(8000, base * ratio))
          biq.gain.value = formantGains[i] * (merged.timbreIntensity ?? 1)
        }
      })
    } catch {}

    // Exciter amount tied to vocoder mode
    try {
      exciterGain.gain.value = merged.vocoderMode ? 0.15 * (merged.timbreIntensity ?? 1) : 0.0
    } catch {}
    delay.delayTime.value = merged.echoDelay
    feedbackGain.gain.value = merged.echo
    wetGain.gain.value = merged.wet
    dryGain.gain.value = merged.dry

    try {
      if (pitchNode && typeof pitchNode.setPitch === 'function') {
        if (!usingWorklet) pitchNode.setPitch(merged.pitch)
      }
    } catch {}

    // Forward parameters to worklet if present
    try {
      if (usingWorklet && workletNode) {
        workletNode.parameters
          .get('pitch')
          ?.setValueAtTime(merged.pitch || 0, audioContext.currentTime)
        workletNode.parameters
          .get('formant')
          ?.setValueAtTime(merged.formant || 0, audioContext.currentTime)
        // Keep worklet wet=1, dry=0 — we handle global wet/dry at graph end
        try {
          workletNode.port.postMessage({
            type: 'params',
            pitch: merged.pitch || 0,
            formant: merged.formant || 0,
            preserve: !!merged.vocoderMode
          })
        } catch {}
      }
    } catch {}

    return merged
  }

  const stop = () => {
    try {
      source.disconnect()
      bassFilter.disconnect()
      pitchNode.input.disconnect()
      pitchNode.output.disconnect()
      delay.disconnect()
      feedbackGain.disconnect()
      wetGain.disconnect()
      dryGain.disconnect()
      destination.disconnect()
    } catch {
      /* ignore */
    }
  }

  return {
    destinationStream: destination.stream,
    updateSettings,
    stop,
    nodes: {
      source,
      bassFilter,
      lowTiltComp,
      highShelf,
      presencePeak,
      formantFilters,
      pitchNode,
      workletNode,
      exciterPreHp,
      exciterShaper,
      exciterPostLp,
      exciterGain,
      delay,
      feedbackGain,
      wetGain,
      dryGain,
      destination
    }
  }
}

export default { createVoiceGraph }
